/**
 * PdfInstructionViewer — centre-screen PDF reader for photo-instruction
 * manuals. Renders one page at a time onto a canvas via pdfjs-dist (already
 * a dependency), so the customer can page through the manual without
 * downloading it or leaving the order page.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Download, Loader2, ZoomIn, ZoomOut } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

// Worker ships with the package; point pdf.js at the copy served from /public
// (copied there at build time by `postinstall`-free plain file copy).
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

export default function PdfInstructionViewer({ url, name, onClose }) {
  const canvasRef = useRef(null);
  const docRef = useRef(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [scale, setScale] = useState(1.2);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(''); setPage(1); setPages(0);
    (async () => {
      try {
        const task = pdfjsLib.getDocument({ url, withCredentials: false });
        const doc = await task.promise;
        if (cancelled) return;
        docRef.current = doc;
        setPages(doc.numPages);
      } catch (e) {
        if (!cancelled) setError('Could not open this PDF.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; docRef.current = null; };
  }, [url]);

  const render = useCallback(async () => {
    const doc = docRef.current;
    const canvas = canvasRef.current;
    if (!doc || !canvas) return;
    const p = await doc.getPage(page);
    const viewport = p.getViewport({ scale: scale * (window.devicePixelRatio || 1) });
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.style.width = `${viewport.width / (window.devicePixelRatio || 1)}px`;
    canvas.style.height = `${viewport.height / (window.devicePixelRatio || 1)}px`;
    await p.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
  }, [page, scale]);

  useEffect(() => { if (pages > 0) render(); }, [pages, render]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setPage((p) => Math.min(p + 1, pages || 1));
      if (e.key === 'ArrowLeft') setPage((p) => Math.max(p - 1, 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, pages]);

  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-2 sm:p-6"
      onClick={onClose}
      data-testid="pdf-instruction-viewer"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[94vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-3 sm:px-4 py-2.5 border-b border-slate-200 bg-slate-50">
          <span className="text-sm font-semibold text-slate-800 truncate flex-1" data-testid="pdf-viewer-title">
            {name || 'Instruction'}
          </span>
          <button
            onClick={() => setScale((s) => Math.max(0.6, +(s - 0.2).toFixed(1)))}
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200" title="Zoom out"
            data-testid="pdf-viewer-zoom-out"
          ><ZoomOut className="w-4 h-4" /></button>
          <button
            onClick={() => setScale((s) => Math.min(3, +(s + 0.2).toFixed(1)))}
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200" title="Zoom in"
            data-testid="pdf-viewer-zoom-in"
          ><ZoomIn className="w-4 h-4" /></button>
          <a
            href={url} download={name || 'instruction.pdf'}
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200" title="Download"
            data-testid="pdf-viewer-download"
          ><Download className="w-4 h-4" /></a>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-500 hover:bg-rose-100 hover:text-rose-600"
            aria-label="Close" data-testid="pdf-viewer-close"
          ><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-auto bg-slate-100 flex items-start justify-center p-3">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-16" data-testid="pdf-viewer-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : error ? (
            <div className="py-16 text-sm text-rose-600" data-testid="pdf-viewer-error">{error}</div>
          ) : (
            <canvas ref={canvasRef} className="shadow-lg bg-white rounded" data-testid="pdf-viewer-canvas" />
          )}
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-center gap-3 px-4 py-2.5 border-t border-slate-200 bg-white">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 disabled:opacity-40 hover:bg-slate-50 flex items-center gap-1"
              data-testid="pdf-viewer-prev"
            ><ChevronLeft className="w-4 h-4" /> Prev</button>
            <span className="text-sm text-slate-600 tabular-nums" data-testid="pdf-viewer-page">
              Page <b className="text-slate-900">{page}</b> / {pages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
              className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 disabled:opacity-40 hover:bg-slate-50 flex items-center gap-1"
              data-testid="pdf-viewer-next"
            >Next <ChevronRight className="w-4 h-4" /></button>
          </div>
        )}
      </div>
    </div>
  );
}
